import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';

const RECAPTCHA_SITE_KEY = '6LcUPFEtAAAAAIa-1gqL2ziVSccxdhdZu40VJjM3';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const { lang } = useParams();
    const { t } = useTranslation();

    const blobsData = useMemo(() => {
        return Array.from({ length: 6 }).map(() => ({
            size: Math.random() * 200 + 150,
            left: Math.random() * 80 + 10,
            top: Math.random() * 80 + 10,
            animationDelay: Math.random() * -20,
            animationDuration: Math.random() * 15 + 15,
        }));
    }, []);

    const blobRefs = useRef([]);

    useEffect(() => {
        const scriptId = 'recaptcha-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://www.google.com/recaptcha/api.js';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            const x = e.clientX / window.innerWidth;
            const y = e.clientY / window.innerHeight;
            blobRefs.current.forEach((blob, index) => {
                if (blob) {
                    const speed = (index + 1) * 20;
                    blob.style.marginLeft = `${x * speed}px`;
                    blob.style.marginTop = `${y * speed}px`;
                }
            });
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const recaptchaResponse = window.grecaptcha && window.grecaptcha.getResponse();
        if (!recaptchaResponse) {
            setError(t('login.errorRecaptcha'));
            return;
        }
        setLoading(true);
        try {
            const response = await fetch('https://orbitronix.ge/api/login.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, recaptcha_response: recaptchaResponse })
            });
            const data = await response.json();
            if (data.success) {
                login(data.user);
                if (data.user.role === 'partner') {
                    navigate(`/${lang || 'en'}/portfolio`);
                } else {
                    navigate('/admin');
                }
            } else {
                setError(data.error || t('login.errorLoginFailed'));
            }
        } catch (err) {
            setError(t('login.errorConnection'));
        }
        setLoading(false);
    };

    return (
        <Layout hideScrollbar minimalFooter fullHeight>
                <div className="mercury-wrapper">
                    <style>{`
                        body {
                            background-color: #010315;
                            color: #fafafa;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                            overflow: hidden;
                            margin: 0;
                        }
                        .mercury-wrapper {
                            width: 100%;
                            flex: 1;
                            overflow: hidden;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            position: relative;
                        }
                        .mercury-wrapper * {
                            box-sizing: border-box;
                        }
                        .stage {
                            position: absolute;
                            width: 100%;
                            height: 100%;
                            z-index: 0;
                            filter: url('#gooey');
                            opacity: 0.5;
                        }
                        .blob {
                            position: absolute;
                            background: linear-gradient(135deg, #a1a1aa, #52525b);
                            border-radius: 50%;
                            filter: blur(40px);
                            animation: float 20s infinite alternate ease-in-out;
                            transition: margin 0.1s ease-out;
                        }
                        @keyframes float {
                            0% { transform: translate(0, 0) scale(1); }
                            33% { transform: translate(10vw, 20vh) scale(1.2); }
                            66% { transform: translate(-5vw, 10vh) scale(0.8); }
                            100% { transform: translate(5vw, -10vh) scale(1.1); }
                        }
                        .auth-card {
                            position: relative;
                            z-index: 10;
                            width: 100%;
                            max-width: 440px;
                            background: rgb(8 12 39 / 50%);
                            border: 1px solid rgb(2 8 50 / 50%);
                            border-radius: 12px;
                            padding: 36px 40px;
                            backdrop-filter: blur(10px);
                            flex-shrink: 0;
                            overflow: hidden;
                            box-sizing: border-box;
                            margin: 0 20px;
                        }
                        @media (max-width: 480px) {
                            .auth-card {
                                padding: 28px 20px;
                                border-radius: 10px;
                                margin: 0 16px;
                            }
                            .auth-card h2 {
                                font-size: 20px;
                            }
                            .form-group input {
                                height: 44px;
                                font-size: 16px;
                            }
                            .btn-submit {
                                height: 44px;
                                font-size: 15px;
                            }
                            .recaptcha-wrap {
                                transform: scale(0.85);
                                transform-origin: center;
                            }
                        }
                        .auth-card h2 {
                            font-size: 24px;
                            font-weight: 600;
                            margin: 0 0 4px;
                            letter-spacing: -0.5px;
                        }
                        .auth-card .subtitle {
                            font-size: 14px;
                            color: #a1a1aa;
                            margin: 0 0 28px;
                        }
                        .form-group {
                            margin-bottom: 20px;
                        }
                        .form-group label {
                            display: block;
                            font-size: 14px;
                            font-weight: 500;
                            margin-bottom: 8px;
                            color: #fafafa;
                        }
                        .form-group input {
                            width: 100%;
                            height: 40px;
                            padding: 0 12px;
                            background: #080c27;
    border: 1px solid #212057;
                            border-radius: 6px;
                            color: #fafafa;
                            font-size: 14px;
                            outline: none;
                            transition: border-color 0.2s, box-shadow 0.2s;
                        }
                        .form-group input::placeholder {
                            color: #71717a;
                        }
                        .form-group input:focus {
                            border-color: #a1a1aa;
                            box-shadow: 0 0 0 2px rgba(161, 161, 170, 0.1);
                        }
                        .password-input-wrapper {
                            position: relative;
                        }
                        .password-input-wrapper input {
                            padding-right: 40px;
                        }
                        .password-toggle {
                            position: absolute;
                            right: 12px;
                            top: 50%;
                            transform: translateY(-50%);
                            background: none;
                            border: none;
                            color: #71717a;
                            cursor: pointer;
                            padding: 0;
                            font-size: 14px;
                            transition: color 0.2s;
                        }
                        .password-toggle:hover {
                            color: #fafafa;
                        }
                        .login-error {
                            background: rgba(239, 68, 68, 0.1);
                            color: #fca5a5;
                            padding: 10px 14px;
                            border-radius: 6px;
                            margin-bottom: 20px;
                            font-size: 14px;
                            border: 1px solid rgba(239, 68, 68, 0.2);
                        }
                        .btn-submit {
                            width: 100%;
                            height: 40px;
                            background: #fafafa;
                            color: #18181b;
                            border: none;
                            border-radius: 6px;
                            font-size: 14px;
                            font-weight: 500;
                            cursor: pointer;
                            transition: background 0.2s;
                            margin-top: 8px;
                        }
                       .btn-submit:hover {
                            background: #6c5ce7 !important;
                            color:#fff;
                        }
                        .btn-submit:disabled {
                            opacity: 0.5;
                            cursor: not-allowed;
                        }
                        .recaptcha-wrap {
                            display: flex;
                            justify-content: center;
                            margin: 20px 0;
                            min-height: 78px;
                        }
                        .footer-links {
                            margin-top: 24px;
                            text-align: center;
                            font-size: 13px;
                            color: #71717a;
                        }
                        .footer-links a {
                            color: #fafafa;
                            text-decoration: none;
                            font-weight: 500;
                        }
                        .footer-links a:hover {
                            text-decoration: underline;
                        }
                        .svg-filter-hidden {
                            position: absolute;
                            width: 0;
                            height: 0;
                        }
                        .orbitronix main {
                            padding-bottom: 0 !important;
                        }
                    `}</style>

                    <svg className="svg-filter-hidden">
                        <defs>
                            <filter id="gooey">
                                <feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur" />
                                <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" result="goo" />
                                <feComposite in="SourceGraphic" in2="goo" operator="atop"/>
                            </filter>
                        </defs>
                    </svg>

                    <div className="stage">
                        {blobsData.map((data, index) => (
                            <div
                                key={index}
                                ref={(el) => (blobRefs.current[index] = el)}
                                className="blob"
                                style={{
                                    width: `${data.size}px`,
                                    height: `${data.size}px`,
                                    left: `${data.left}%`,
                                    top: `${data.top}%`,
                                    animationDelay: `${data.animationDelay}s`,
                                    animationDuration: `${data.animationDuration}s`,
                                }}
                            />
                        ))}
                    </div>

                    <div className="auth-card">
                        <h2>{t('login.welcomeBack')}</h2>
                        <p className="subtitle">{t('login.subtitle')}</p>

                        {error && <div className="login-error">{error}</div>}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>{t('login.username')}</label>
                                <input
                                    type="text"
                                    placeholder={t('login.usernamePlaceholder')}
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('login.password')}</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('login.passwordPlaceholder')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                    >
                                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                            </div>

                            <div className="recaptcha-wrap">
                                <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} data-theme="dark"></div>
                            </div>

                            <button type="submit" className="btn-submit" disabled={loading}>
                                {loading ? t('login.signingIn') : t('login.signIn')}
                            </button>
                        </form>

                       
                    </div>
                    </div>
        </Layout>
    );
};

export default LoginPage;
